import { z } from 'zod';

import { matchesAcceptTypes } from './matchesAcceptTypes';

/**
 * Canonical accept maps for upload validation and UI `accept` props.
 *
 * Kept in this file (rather than re-exported from a sibling) so bundlers group
 * them in the same chunk as `getZodFileUploadSchema`. Splitting them into a
 * separate chunk causes them to be `undefined` when a consumer calls
 * `getZodFileUploadSchema(acceptImages, ...)` at module init time.
 */
export const acceptImages: Record<string, string[]> = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
};

export const acceptDocuments: Record<string, string[]> = {
  'application/pdf': ['.pdf'],
};

/** Legacy aliases used by older portal `UploadDropzone` components. */
export const acceptPDF = acceptDocuments;

export const acceptJPG: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
};

export const acceptPNG: Record<string, string[]> = {
  'image/png': ['.png'],
};

export const acceptBMP: Record<string, string[]> = {
  'image/bmp': ['.bmp'],
};

const FALLBACK_ACCEPT_TYPES: Record<string, string[]> = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'application/pdf': ['.pdf'],
};

const DEFAULT_ACCEPT_TYPES: Record<string, string[]> = FALLBACK_ACCEPT_TYPES;

/** Minimal translate fn — compatible with i18next `t`. */
export type FileUploadTranslate = (key: string, options?: Record<string, unknown>) => string;

export type FileUploadErrorMessages = {
  acceptTypes?: string;
  maxFileSize?: string;
  fileType?: string;
  required?: string;
  /** Override message when too many files are uploaded. */
  maxFiles?: string;
  /** Override message when too few files are uploaded. */
  minFiles?: string;
};

export type FileUploadSchemaOptions = FileUploadErrorMessages & {
  /**
   * Maximum number of files allowed. Form-specific — pass the same value used
   * for the Upload `maxFiles` prop.
   */
  maxFileCount?: number;
  /**
   * Minimum number of files required. Omit for optional uploads (chain
   * `.optional()` at the call site when empty/undefined must be valid).
   */
  minFileCount?: number;
  /** Used for default min/max count messages when no override is provided. */
  t?: FileUploadTranslate;
};

const EMPTY_ACCEPT_MAP_MESSAGE = 'getZodFileUploadSchema: acceptTypes was empty at schema construction. '
  + 'This usually means the accept map was undefined due to a bundler chunk-init order issue. '
  + 'Falling back to images + PDF; verify the call site imports the accept map from '
  + '`@woonstad/helpers-form-utils`.';

function resolveAcceptTypes(
  acceptTypes: Record<string, string[]> | null | undefined,
): Record<string, string[]> {
  if (acceptTypes && Object.keys(acceptTypes).length > 0) {
    return acceptTypes;
  }

  // Log loudly so monitoring/consoles surface the misconfiguration, but fall
  // back to a safe default so the app does not fail to boot.
  // eslint-disable-next-line no-console
  console.error(EMPTY_ACCEPT_MAP_MESSAGE);

  return DEFAULT_ACCEPT_TYPES ?? FALLBACK_ACCEPT_TYPES;
}

function formatSizeMb(maxFileSize: number): string | number {
  const sizeMb = maxFileSize / 1024 / 1024;

  return Number.isInteger(sizeMb) ? sizeMb : Number(sizeMb.toFixed(2));
}

function defaultMaxFilesMessage(amount: number, t?: FileUploadTranslate): string {
  if (t) {
    return t('Upload maximaal {{amount}} bestanden', { amount });
  }

  return `Upload maximaal ${amount} bestanden`;
}

function defaultMinFilesMessage(amount: number, t?: FileUploadTranslate): string {
  if (t) {
    return t('Upload minimaal {{amount}} bestand', { amount });
  }

  return `Upload minimaal ${amount} bestand`;
}

/**
 * Builds the standard file-upload Zod error messages via `t`.
 * Dutch strings are used as keys (portal i18n convention).
 */
export function getFileUploadErrorMessages(
  t: FileUploadTranslate,
  acceptTypes: Record<string, string[]> | null | undefined,
  maxFileSize: number,
  overrides?: FileUploadErrorMessages,
): FileUploadErrorMessages {
  const resolvedAcceptTypes = resolveAcceptTypes(acceptTypes);
  const types = Object.values(resolvedAcceptTypes).flat().join(', ');

  return {
    acceptTypes:
      overrides?.acceptTypes
      ?? t('{{types}} bestanden worden geaccepteerd.', { types }),
    maxFileSize:
      overrides?.maxFileSize
      ?? t('Maximum bestandsgrootte is {{size}}MB', { size: formatSizeMb(maxFileSize) }),
    fileType:
      overrides?.fileType
      ?? t('Verkeerd type gevonden, upload het bestand opnieuw.'),
    required: overrides?.required,
    maxFiles: overrides?.maxFiles,
    minFiles: overrides?.minFiles,
  };
}

export function getZodFileUploadSchema(
  acceptTypes: Record<string, string[]> | null | undefined,
  maxFileSize: number,
  options?: FileUploadSchemaOptions,
) {
  const resolvedAcceptTypes = resolveAcceptTypes(acceptTypes);

  const types = Object.values(resolvedAcceptTypes).flat().join(', ');

  const acceptMessage = options?.acceptTypes
    ?? `${types} bestanden worden geaccepteerd.`;

  const fileSizeMessage = options?.maxFileSize
    ?? `Maximum bestandsgrootte is ${formatSizeMb(maxFileSize)}MB`;

  const fileTypeMessage = options?.fileType
    ?? 'Verkeerd type gevonden, upload het bestand opnieuw.';

  let schema = z.array(
    z.any()
      .refine((file: File) => file instanceof File, fileTypeMessage)
      .refine(
        (file: File) => matchesAcceptTypes(file.type, file.name, resolvedAcceptTypes),
        acceptMessage,
      )
      .refine(
        (file: File) => file instanceof File && file.size <= maxFileSize,
        fileSizeMessage,
      ),
    {
      error: options?.required ?? undefined,
    },
  );

  if (options?.minFileCount != null) {
    schema = schema.min(
      options.minFileCount,
      options.minFiles ?? defaultMinFilesMessage(options.minFileCount, options.t),
    );
  }

  if (options?.maxFileCount != null) {
    schema = schema.max(
      options.maxFileCount,
      options.maxFiles ?? defaultMaxFilesMessage(options.maxFileCount, options.t),
    );
  }

  return schema;
}

export type DataURLFileLike = {
  name: string;
  mimeType: string;
  size: number;
};

export type DataURLFileUploadSchemaOptions = FileUploadErrorMessages & {
  /** When true, `undefined` / `null` values pass validation. */
  optional?: boolean;
  t?: FileUploadTranslate;
};

export function getDataURLFileUploadSchema(
  acceptTypes: Record<string, string[]> | null | undefined,
  maxFileSize: number,
  options?: DataURLFileUploadSchemaOptions,
) {
  const resolvedAcceptTypes = resolveAcceptTypes(acceptTypes);

  const types = Object.values(resolvedAcceptTypes).flat().join(', ');

  const acceptMessage = options?.acceptTypes
    ?? `${types} bestanden worden geaccepteerd.`;

  const fileSizeMessage = options?.maxFileSize
    ?? `Maximum bestandsgrootte is ${formatSizeMb(maxFileSize)}MB`;

  const requiredMessage = options?.required
    ?? options?.t?.('Vul dit veld in.')
    ?? 'Vul dit veld in.';

  let schema = z.custom<DataURLFileLike | undefined>();

  if (!options?.optional) {
    schema = schema.refine(
      (file) => file !== undefined && file !== null,
      requiredMessage,
    );
  }

  return schema
    .refine((file) => {
      if (file === undefined || file === null) {
        return true;
      }

      return matchesAcceptTypes(file.mimeType, file.name, resolvedAcceptTypes);
    }, acceptMessage)
    .refine((file) => {
      if (file === undefined || file === null) {
        return true;
      }

      return file.size <= maxFileSize;
    }, fileSizeMessage);
}
